const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.citycourier.org/api/v1';

export interface Client {
    id: number;
    name: string;
    email: string;
    loginID: string;
    type: 'CLIENT';
    status: number;
    created_at: string;
    branch_id?: number;
    clientProfile?: ClientProfile;
    client_profile?: ClientProfile; // API returns snake_case
    branch?: Branch;
}

export interface ClientProfile {
    id: number;
    user_id: number;
    account_no?: string;
    area_zone?: number | City; // Can be ID or nested object
    point_of_sale?: number | City; // Can be ID or nested object
    account_type?: string;
    business_registration_status?: string;
    company_name?: string;
    address?: string;
    authorized_person_name?: string;
    authorized_designation?: string;
    authorized_phone?: string;
    authorized_cnic?: string;
    admin_contact_name?: string;
    admin_phone?: string;
    finance_contact_name?: string;
    web_address?: string;
    brand_name?: string;
    ntn?: string;
    gst?: string;
    gst_enabled?: boolean;
    business_sector?: string;
    products?: string;
    pickup_address?: string;
    pickup_contact_person?: string;
    pickup_phone?: string;
    pickup_email?: string;
    // Relations - API may return camelCase or snake_case
    areaZone?: City;
    pointOfSale?: City;
}

export interface Branch {
    id: number;
    name: string;
    code: string;
}

export interface City {
    id: number;
    name: string;
    code: string;
}

export interface ClientTariff {
    id: number;
    user_id: number;
    service_type: string;
    order_type: string;
    weight: string;
    same_city: number;
    same_zone: number;
    diff_zone: number;
    created_at: string;
    updated_at: string;
}

export interface ClientTariffsResponse {
    tariffs: ClientTariff[];
}

export interface CreateClientData {
    name: string;
    email: string;
    branch_id?: number;
    password: string;
    company_name?: string;
    address?: string;
    area_zone?: number;
    point_of_sale?: number;
    account_type?: string;
    business_registration_status?: string;
    authorized_person_name?: string;
    authorized_designation?: string;
    authorized_phone?: string;
    authorized_cnic?: string;
    admin_contact_name?: string;
    admin_phone?: string;
    finance_contact_name?: string;
    web_address?: string;
    brand_name?: string;
    ntn?: string;
    gst?: string;
    gst_enabled?: boolean;
    business_sector?: string;
    products?: string;
    pickup_address?: string;
    pickup_contact_person?: string;
    pickup_phone?: string;
    pickup_email?: string;
}

export interface UpdateClientData {
    name?: string;
    email?: string;
    branch_id?: number;
    company_name?: string;
    address?: string;
    area_zone?: number;
    point_of_sale?: number;
    account_type?: string;
    business_registration_status?: string;
    authorized_person_name?: string;
    authorized_designation?: string;
    authorized_phone?: string;
    authorized_cnic?: string;
    admin_contact_name?: string;
    admin_phone?: string;
    finance_contact_name?: string;
    web_address?: string;
    brand_name?: string;
    ntn?: string;
    gst?: string;
    gst_enabled?: boolean;
    business_sector?: string;
    products?: string;
    pickup_address?: string;
    pickup_contact_person?: string;
    pickup_phone?: string;
    pickup_email?: string;
}

export interface ClientsListParams {
    status?: string;
    query?: string;
    page?: number;
    per_page?: number;
}

export interface ClientsListResponse {
    clients: Client[];
    pagination: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from: number;
        to: number;
    };
}

export interface ApiResponse<T> {
    success: boolean;
    data: T;
    message: string;
}

export interface BackendResponse<T> {
    success: boolean;
    data: T;
    message: string;
}

// Helper function to get auth token
const getAuthToken = (): string | null => {
    return localStorage.getItem('token');
};

// Helper function to handle API errors
const handleApiError = async (response: Response): Promise<never> => {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
};

export const clientApi = {
    // Get clients list with filtering and pagination
    getClients: async (params: ClientsListParams = {}): Promise<ClientsListResponse> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, value.toString());
            }
        });

        const response = await fetch(`${API_BASE_URL}/clients?${queryParams}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<ClientsListResponse> = await response.json();
        return data.data;
    },

    // Get single client details
    getClient: async (id: number): Promise<Client> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/clients/${id}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ client: Client }> = await response.json();
        return data.data.client;
    },

    // Create new client
    createClient: async (clientData: CreateClientData): Promise<Client> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/clients`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(clientData),
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ client: Client }> = await response.json();
        return data.data.client;
    },

    // Update client
    updateClient: async (id: number, clientData: UpdateClientData): Promise<Client> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/clients/${id}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(clientData),
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ client: Client }> = await response.json();
        return data.data.client;
    },

    // Delete client
    deleteClient: async (id: number): Promise<void> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/clients/${id}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }
    },

    // Toggle client status (activate/deactivate)
    toggleClientStatus: async (id: number): Promise<{ id: number; name: string; status: number }> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/clients/${id}/toggle-status`, {
            method: 'PATCH',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ client: { id: number; name: string; status: number } }> = await response.json();
        return data.data.client;
    },

    // Approve client
    approveClient: async (id: number): Promise<Client> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/clients/${id}/approve`, {
            method: 'PATCH',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ client: Client }> = await response.json();
        return data.data.client;
    },

    // Get branches list
    getBranches: async (): Promise<Branch[]> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/clients/branches`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ branches: Branch[] }> = await response.json();
        return data.data.branches;
    },

    // Get cities list
    getCities: async (): Promise<City[]> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/clients/cities`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<{ cities: City[] }> = await response.json();
        return data.data.cities;
    },

    // Get client tariffs
    getClientTariffs: async (clientId: number): Promise<ClientTariff[]> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/clients/${clientId}/tariffs`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<ClientTariffsResponse> = await response.json();
        return data.data.tariffs;
    },

    // Update client tariffs
    updateClientTariffs: async (clientId: number, tariffs: Omit<ClientTariff, 'id' | 'user_id' | 'created_at' | 'updated_at'>[]): Promise<ClientTariff[]> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');

        const response = await fetch(`${API_BASE_URL}/clients/${clientId}/tariffs`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ tariffs }),
        });

        if (!response.ok) {
            await handleApiError(response);
        }

        const data: BackendResponse<ClientTariffsResponse> = await response.json();
        return data.data.tariffs;
    },
};
